import React, { Fragment, useContext, useEffect, useState } from 'react'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { PermissionsEnum } from 'api/Identity/Types/PermissionsEnum'

import { AuthSessionContext } from '../../../../../utils/AuthContext'
import { hasPermission } from '../../../../../utils/permissionsValidator'
import { ClientNavigation, ClientTab } from '../../Navigation/ClientNavigation'
import { ClientDetailsBankAccountsTab } from './ClientDetailsBankAccountsTab'
import { ClientDetailsTokenizedCardsTab } from './ClientDetailsTokenizedCardsTab'
import { ClientDetailsBankAccountsPageProps } from './types'

enum PaymentDetailsSubTab {
  BANK_ACCOUNTS = 'bank-accounts',
  CARDS = 'cards',
}

const ClientDetailsBankAccountsPage: React.FC<ClientDetailsBankAccountsPageProps> = (props) => {
  const { data, updateClientStatus } = props

  const [auth] = useContext(AuthSessionContext)
  const permissions = auth?.permissions || []

  const canViewBankAccounts = hasPermission(permissions, PermissionsEnum.CanViewClientAccounts)
  const canBrowseTokenizedCards = hasPermission(
    permissions,
    PermissionsEnum.CanBrowseTokenizedCardDetails
  )

  const [activeSubTab, setActiveSubTab] = useState<PaymentDetailsSubTab>(
    canViewBankAccounts ? PaymentDetailsSubTab.BANK_ACCOUNTS : PaymentDetailsSubTab.CARDS
  )

  useEffect(() => {
    if (activeSubTab === PaymentDetailsSubTab.BANK_ACCOUNTS && !canViewBankAccounts) {
      setActiveSubTab(PaymentDetailsSubTab.CARDS)
    }

    if (activeSubTab === PaymentDetailsSubTab.CARDS && !canBrowseTokenizedCards) {
      setActiveSubTab(PaymentDetailsSubTab.BANK_ACCOUNTS)
    }
  }, [activeSubTab, canBrowseTokenizedCards, canViewBankAccounts])

  const handleSubTabChange = (tab: string) => {
    if (tab === PaymentDetailsSubTab.BANK_ACCOUNTS || tab === PaymentDetailsSubTab.CARDS) {
      setActiveSubTab(tab)
    }
  }

  return (
    <Fragment>
      <ClientNavigation
        isEdit={false}
        data={data}
        tab={ClientTab.BANK_ACCOUNTS}
        updateClientStatus={updateClientStatus}
      />
      <Tabs value={activeSubTab} onValueChange={handleSubTabChange} className='mt-2'>
        <TabsList>
          {canViewBankAccounts && (
            <TabsTrigger value={PaymentDetailsSubTab.BANK_ACCOUNTS}>Bank Accounts</TabsTrigger>
          )}
          {canBrowseTokenizedCards && (
            <TabsTrigger value={PaymentDetailsSubTab.CARDS}>Cards</TabsTrigger>
          )}
        </TabsList>
      </Tabs>

      {activeSubTab === PaymentDetailsSubTab.BANK_ACCOUNTS && canViewBankAccounts && (
        <ClientDetailsBankAccountsTab {...props} />
      )}
      {activeSubTab === PaymentDetailsSubTab.CARDS && canBrowseTokenizedCards && (
        <ClientDetailsTokenizedCardsTab clientId={data.id} />
      )}
    </Fragment>
  )
}

export default ClientDetailsBankAccountsPage
